import { PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeEvery, takeLatest } from 'redux-saga/effects';
import { createStore, exitImpersionateUser, getDealerActionTimeoutAPI, getStoreDetails, impersionateUser, updateSOTStampPath, updateLOAPath, getStoreList, pushTracking, addDealerEvent } from '@/api/apis';
import { dealerActionTimeout, setCreateStoreData, setDealerActionTimeout, setDomainError, setEmailError, setStoreLicenseError, setIsCreateStoreLoading, setIsImpersonating, setStoreData, setStoreRefresh, storeDataInitialValue, setIsProcessCompleted, setRefreshStatus, setStoreList, setIsAddDealerEventLoading, setAddDealerEventSuccess } from './app-slice';
import type { AddDealerEventPayload } from './app-slice';
import StoreDetailsInterface, { TrackingInterface } from '@/model/store.mjs';
import { ToasterNotification } from '@/toaster/toasterNotification';
import { setAutomateTransferWorkflow,setIsMultiTenantStore } from '@/storeAdmin/ducks/slice';
import { ErrorToasterNotification, SimpleToasterNotification } from '@/toaster/successNotification';
import { CREATE_STORE_DOMAIN_ERROR, CREATE_STORE_EMAIL_ERROR, CREATE_STORE_LICENSE_ID_ERROR } from '@/utils/constants';
import { NavigateFunction } from 'react-router';
import { AxiosResponse } from 'axios';

function* postCreateStore(action: PayloadAction<StoreDetailsInterface>) {
  try {
    //@ts-ignore
    const response = yield call(createStore, action.payload);
    if (response?.status === 200) {
      console.log("response: ", response.data)
      yield put(setCreateStoreData(response.data));
      yield put(setStoreRefresh(true));
      ToasterNotification('success', "Store created successfully")
    }
  } catch (error) {
    console.log('error', error);
    ToasterNotification('error', "Found error. Try Again Later")
    yield put(setCreateStoreData(storeDataInitialValue));
  }
}

function* onCreateStore(action: PayloadAction<any>) {
  try {
    yield put(setIsCreateStoreLoading(true));
    //@ts-ignore
    const response = yield call(createStore, action.payload);
    if (response?.status === 200) {
      console.log("response: ", response.data)
      yield put(setCreateStoreData(response.data));
      yield put(setStoreRefresh(true));
      yield put(setIsCreateStoreLoading(false));
      SimpleToasterNotification('success', "Store created successfully")
    }
  } catch (error: any) {
    yield put(setIsCreateStoreLoading(false));
    console.log('error', error);
    const errorMsg = "Found error. Try Again Later";
    if (error.response.data?.Error && error.response.data?.Error === CREATE_STORE_LICENSE_ID_ERROR) {
      yield put(setStoreLicenseError(error.response.data?.Error));
    } else if(error.response.data?.Error && error.response.data?.Error === CREATE_STORE_DOMAIN_ERROR) {
      yield put(setDomainError(error.response.data?.Error));
    } else if(error.response.data?.Error && error.response.data?.Error === CREATE_STORE_EMAIL_ERROR) {
      yield put(setEmailError(error.response.data?.Error));
    } else {
      ErrorToasterNotification(errorMsg)
    }
    yield put(setCreateStoreData(storeDataInitialValue));
  }
}

function* getStoreData(action: PayloadAction<StoreDetailsInterface>) {
  try {
    //@ts-ignore
    const response = yield call(getStoreDetails, action.payload);
    if (response?.status === 200) {
      yield put(setStoreData(response.data));
      yield put(setAutomateTransferWorkflow(response.data.is_pending_workflow))
      yield put(setIsMultiTenantStore(response.data?.is_multi_tenant))
    }
  } catch (error) {
    console.log('error', error);
    yield put(setStoreData(storeDataInitialValue));
    yield put(setAutomateTransferWorkflow(false))
    yield put(setIsMultiTenantStore(false))
  }
}

function* callToImpersonateGlobalAdmin(action: PayloadAction<{ storeId: string; navigate: NavigateFunction }>) {
  try {
    const { storeId, navigate } = action.payload;
    const response: AxiosResponse<{}> = yield call(impersionateUser, { storeId });
    if (response?.status === 200) {
      navigate("/");
      window.location.reload();
    }
  } catch (error) {
    console.log('error', error);
    yield put(setStoreData(storeDataInitialValue));
    yield put(setAutomateTransferWorkflow(false))
    yield put(setIsMultiTenantStore(false))
  }
}


function* stopImpersonateGlobalAdmin(action: PayloadAction<{ navigate: NavigateFunction }>) {
  try {
    const response: AxiosResponse<{}> = yield call(exitImpersionateUser);
    if (response?.status === 200) {
      yield put(setIsImpersonating(false));
      action.payload.navigate("/");
      window.location.reload();
    }
  } catch (error) {
    console.log('error', error);
  }
}


function* getDealerActionTimeoutDataFun() {
  try {
    //@ts-ignore
    const response = yield call(getDealerActionTimeoutAPI);
    if (response?.status === 200) {
      yield put(setDealerActionTimeout(response.data));
    }
  } catch (error) {
    console.log('error', error);
    yield put(setDealerActionTimeout(dealerActionTimeout));
  }
}
function* callToUpdateLOAPath(action: PayloadAction<{licenseKey:string; licensePath: string; source: string}>){
  try {
    console.log({LOA: action.payload});
    // @ts-ignore
    const response = yield call(updateLOAPath, action.payload)
    if(response?.status === 200){
      yield put(setIsProcessCompleted(true))
      yield put(setRefreshStatus(true));
    }
    else{
      ToasterNotification("error", "Failed to upload LOA ");
      yield put(setIsProcessCompleted(false))
      yield put(setRefreshStatus(true));
    }
  } catch (error) {
    ToasterNotification("error", "Failed to upload LOA ");
    yield put(setIsProcessCompleted(false))
    yield put(setRefreshStatus(true));
  }
}
function* callToUpdateSOTPath(action: PayloadAction<{licenseKey:string; licensePath: string, source: string}>){
  try {
    // @ts-ignore
    const response = yield call(updateSOTStampPath, action.payload)
    if(response?.status === 200){
      yield put(setIsProcessCompleted(true))
      yield put(setRefreshStatus(true));
    }
    else{
      ToasterNotification("error", "Failed to upload SOT ");
      yield setIsProcessCompleted(false)
      yield put(setRefreshStatus(true));
    }
  } catch (error) {
    ToasterNotification("error", "Failed to upload SOT ");
    yield setIsProcessCompleted(false)
    yield put(setRefreshStatus(true));
  }
}

function* getStoreListData() {
  try {
    //@ts-ignore
    const response = yield call(getStoreList);
    if (response?.status === 200) {
      yield put(setStoreList(response.data));
    }
  } catch (error) {
    console.log('error', error);
    yield put(setStoreList([]));
  }
}

function* getTrackingData(action: PayloadAction<TrackingInterface>) {
  try {
    //@ts-ignore
   
    const response = yield call(pushTracking, action.payload);
    console.log("Tracking response: ", response)
    if (response?.status === 204) {
      console.log("Tracking data pushed successfully");
    }
  } catch (error) {
    console.log('error', error);
  }
}

function* addDealerEventSaga(action: PayloadAction<AddDealerEventPayload>) {
  try {
    yield put(setIsAddDealerEventLoading(true));
    //@ts-ignore
    const response = yield call(addDealerEvent, action.payload);
    if (response?.status === 201 || response?.status === 200) {
      yield put(setIsAddDealerEventLoading(false));
      yield put(setAddDealerEventSuccess(true));
      SimpleToasterNotification('success', 'Event added successfully');
    }
  } catch (error: any) {
    yield put(setIsAddDealerEventLoading(false));
    console.log('error', error);
    const defaultMsg = 'We couldn\'t save the event. Please check your details and try again.';
    const apiMsg = error?.response?.status === 404 && (
      error?.response?.data?.message ??
      error?.response?.data?.error ??
      error?.response?.data?.Error ??
      error?.response?.data?.detail
    );
    ErrorToasterNotification(typeof apiMsg === 'string' ? apiMsg : defaultMsg);
  }
}

function* watchCallToPostCreateStore() {
  yield takeEvery('app/callToGetCreateStoreApi', postCreateStore);
}

function* watchCallToCreateStore() {
  yield takeEvery('app/callToCreateStore', onCreateStore);
}

function* watchCallToGetCreateStore() {
  yield takeEvery('app/callToGetStoreData', getStoreData);
}

function* watchCallToStopImpersonateGlobalAdmin() {
  yield takeEvery('app/callToStopImpersonateGlobalAdmin', stopImpersonateGlobalAdmin);
}

function* watchCallToImpersonateGlobalAdmin() {
  yield takeEvery('app/callToImpersonateGlobalAdmin', callToImpersonateGlobalAdmin);
}

function* watchCallToGetDealerActionTimeout() {
  yield takeEvery('app/callToGetDealerAction', getDealerActionTimeoutDataFun);
}
function* watchForCallToUpdateSOTPath() {
  yield takeEvery("app/updateSOTLicensePath", callToUpdateSOTPath)
}
function* watchForCallToUpdateLOAPath() {
  yield takeEvery("app/updateLOALicensePath", callToUpdateLOAPath)
}
function* watchCallToGetStoreList() {
  yield takeEvery('app/callToGetStoreList', getStoreListData);
}


function* watchCallToGetTrackingData() { 
  yield takeEvery('app/callToGetTrackingData', getTrackingData);
}

function* watchCallToAddDealerEvent() {
  yield takeEvery('app/callToAddDealerEvent', addDealerEventSaga);
}

export { watchCallToPostCreateStore, watchCallToGetCreateStore, watchCallToCreateStore, watchCallToStopImpersonateGlobalAdmin, watchCallToImpersonateGlobalAdmin, watchCallToGetDealerActionTimeout, watchForCallToUpdateSOTPath, watchForCallToUpdateLOAPath, watchCallToGetStoreList, watchCallToGetTrackingData, watchCallToAddDealerEvent };